import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  TextField
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PrimaryButton, SecondaryButton } from '@components';
import { useUser } from '@hooks/useUser';
import { editOperationIncidentNote, OperationIncidentNote } from '../../../../networkRequest/operationsService';
import Utils from '../../../../utils/utils';
import { parseActivityIncidentFormData, defaultActivityIncidentFormErrors, validateActivityIncidentField, ActivityIncidentFormData, ActivityIncidentFormErrors } from '../../utils/formValidation';

dayjs.extend(customParseFormat);

interface EditIncidentNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onEditIncidentNote: () => void;
  selectedIncidentNote: OperationIncidentNote | null;
  setSelectedIncidentNote: Dispatch<SetStateAction<OperationIncidentNote | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

const EditIncidentNoteDialog = ({
  open,
  onClose,
  onEditIncidentNote,
  selectedIncidentNote,
  setSelectedIncidentNote,
  setError,
}: EditIncidentNoteDialogProps) => {
  const { user } = useUser();

  const [formErrors, setFormErrors] = useState<ActivityIncidentFormErrors>(defaultActivityIncidentFormErrors);
  const [formData, setFormData] = useState<ActivityIncidentFormData>({
    date: dayjs(),
    time: dayjs(),
    description: '',
  });
  const [saving, setSaving] = useState<boolean>(false);
  const formRef = useRef(null);
  const hasErrors = Object.values(formErrors).some((error) => error);

  useEffect(() => {
    if (!selectedIncidentNote) return;
    setFormData({
      date: dayjs(selectedIncidentNote?.performed_at),
      time: dayjs(selectedIncidentNote?.performed_at),
      description: selectedIncidentNote?.description ?? '',
    });
  }, [selectedIncidentNote]);

  const handleCancel = () => {
    setSelectedIncidentNote(null);
    setFormErrors(defaultActivityIncidentFormErrors);
    onClose();
  };

  const handleChangeInput = (field: keyof typeof formData, value: any) => {
    const newFormData = { ...formData, [field]: value };
    validateActivityIncidentField({ field, value, stateHandler: setFormErrors, formErrors });
    setFormData(newFormData);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedIncidentNote || !user || hasErrors) return;

    const { date, time, description } = parseActivityIncidentFormData(formData);

    try {
      setSaving(true);
      const { data, error } = await editOperationIncidentNote({
        accessToken: user?.accessToken,
        noteId: selectedIncidentNote?.id,
        params: {
          performed_at: Utils.createTimestamp(date, time),
          description,
        }
      });

      if (data) {
        onEditIncidentNote();
        setSelectedIncidentNote(null);
        setFormErrors(defaultActivityIncidentFormErrors);
        onClose();
      }

      if (error) {
        setError(error);
        return;
      }
    } catch {
      setError('Failed to update incident note');
    } finally {
      setSaving(false);
    }
  }

  if (!selectedIncidentNote) {
    return null;
  }

  return (
    <Dialog
      open={open}
      maxWidth='md'
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Incident Note
      </DialogTitle>

      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ padding: '40px 24px 36px 24px' }}>
          <Grid container sx={{
            mt: '8px'
          }}>
            <Grid size={12} container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name='date'
                    label='Date'
                    value={formData.date}
                    onChange={(value) => handleChangeInput('date', value)}
                    disableFuture
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.date,
                        onChange: (value) => handleChangeInput('date', value),
                        helperText: formErrors.date ?? '',
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                        slotProps: { inputLabel: { shrink: true } },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    name='time'
                    label='Time'
                    value={formData.time}
                    onChange={(value) => handleChangeInput('time', value)}
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.time,
                        onChange: (value) => handleChangeInput('time', value),
                        helperText: formErrors.time ?? '',
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid size={12} sx={{
              mt: '16px'
            }}>
              <TextField
                disabled={saving}
                multiline
                fullWidth
                required
                name='description'
                label='Description'
                error={!!formErrors.description}
                onChange={(e) => handleChangeInput('description', e.target.value)}
                helperText={formErrors.description ?? ''}
                value={formData.description}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
        >
          <SecondaryButton action={handleCancel}>
            Cancel
          </SecondaryButton>

          <PrimaryButton
            type='submit'
            disabled={saving || hasErrors}
          >
            Submit
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditIncidentNoteDialog;